import { useEffect, useRef, useState } from "react";

export const BID_ANIMATION_DURATION_MS = 500;

/** The easing of cubic-bezier(0.16, 1, 0.3, 1), with time on its x axis. */
export function bidAnimationProgress(progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;
  let lower = 0;
  let upper = 1;
  // Solve x(t) before reading y(t): substituting elapsed time directly as t
  // would produce a different curve from the CSS timing function.
  for (let iteration = 0; iteration < 20; iteration++) {
    const t = (lower + upper) / 2;
    const x = 3 * (1 - t) ** 2 * t * 0.16 + 3 * (1 - t) * t ** 2 * 0.3 + t ** 3;
    if (x < progress) lower = t;
    else upper = t;
  }
  return 1 - (1 - (lower + upper) / 2) ** 3;
}

/** Presentation only: neither bid submission nor tax quotes read these values. */
export function useAnimatedBids(targets: Record<string, number>): Record<string, number> {
  const [displayed, setDisplayed] = useState(targets);
  const displayedRef = useRef(displayed);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );
  // Content identity avoids restarting the animation on its own render ticks.
  const targetKey = JSON.stringify(targets);

  useEffect(() => {
    const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!preference) return;
    const update = () => setReducedMotion(preference.matches);
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const next: Record<string, number> = JSON.parse(targetKey);
    const started = Date.now();
    const animations = Object.entries(next).map(([id, target]) => {
      const from = displayedRef.current[id] ?? target;
      return { id, target, from: Math.round(from * 100), to: Math.round(target * 100) };
    });
    const hasChanges = animations.some(({ from, to }) => from !== to);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      const elapsed = Math.max(0, Date.now() - started);
      const complete = reducedMotion || !hasChanges || elapsed >= BID_ANIMATION_DURATION_MS;
      const progress = complete ? 1 : bidAnimationProgress(elapsed / BID_ANIMATION_DURATION_MS);
      const values: Record<string, number> = {};
      for (const animation of animations) {
        const { id, target, from, to } = animation;
        values[id] = complete ? target : Math.round(from + (to - from) * progress) / 100;
      }
      displayedRef.current = values;
      setDisplayed(values);
      if (!complete) timer = setTimeout(tick, Math.min(16, BID_ANIMATION_DURATION_MS - elapsed));
    };
    tick();
    // A hover reversal starts at the last visible value. The old direction
    // loses its only timer, including when the card or entire board disappears.
    return () => clearTimeout(timer);
  }, [targetKey, reducedMotion]);

  return displayed;
}
